/**
 * Fixed accounting taxonomy — the frontend source of truth for category,
 * department, and account-kind metadata until the backend exposes them.
 *
 * `contracts.ts` imports `CATEGORY_IDS` (value) to build the `CategoryId` zod
 * enum; this file type-imports the enum types back from `contracts.ts`
 * (type-only, erased at runtime — no runtime import cycle).
 *
 * NO inline UI labels: every human string is an i18n key under the `accounting`
 * namespace (`accounting:category.*`, `accounting:department.*`,
 * `accounting:account_kind.*`). Colours are wire-level (donut/tag tints) and
 * deliberately NOT theme tokens — they identify a category across light/dark.
 */

import type { TxnType, Department, AccountKind } from './contracts'

// ─── Categories ───────────────────────────────────────────────────────────────
// Seeded from prototype CATEGORIES. id prefix encodes type: c-* income, e-* expense.

export const CATEGORY_IDS = [
  // income
  'c-prod', // Маҳсулот сотуви
  'c-srv', // Хизматлар
  'c-other', // Бошқа кирим
  'c-loan', // Кредит/Қарз
  // expense
  'e-mat', // Хом ашё/Материал
  'e-trans', // Транспорт
  'e-zp', // Иш ҳақи
  'e-util', // Коммунал
  'e-rent', // Ижара
  'e-tax', // Солиқ/божлар
  'e-other', // Бошқа харажат
] as const

export type CategoryId = (typeof CATEGORY_IDS)[number]

export interface CategoryMeta {
  id: CategoryId
  type: TxnType
  /** i18n key under the `accounting` namespace, e.g. `category.c-prod`. */
  labelKey: string
  /** Wire-level identity colour for donut slices + category tags. */
  color: string
}

// Colour values are ported verbatim from the standalone prototype
// (`temp_files/GlassproStandalone2.html`, blob 0 `CATEGORIES`). They identify
// the slice/tag across light and dark themes, so they are intentionally raw
// hex (not AntD theme tokens — see file header). Reviewer guidance in the
// continuation doc: "Status/category colors come from the prototype".
export const CATEGORY_TAXONOMY: readonly CategoryMeta[] = [
  // income
  { id: 'c-prod', type: 'INCOME', labelKey: 'category.c-prod', color: '#1b8a4d' },
  { id: 'c-srv', type: 'INCOME', labelKey: 'category.c-srv', color: '#2272d6' },
  { id: 'c-other', type: 'INCOME', labelKey: 'category.c-other', color: '#9b6bd1' },
  { id: 'c-loan', type: 'INCOME', labelKey: 'category.c-loan', color: '#d9a635' },
  // expense
  { id: 'e-mat', type: 'EXPENSE', labelKey: 'category.e-mat', color: '#d9533a' },
  { id: 'e-trans', type: 'EXPENSE', labelKey: 'category.e-trans', color: '#ef8a17' },
  { id: 'e-zp', type: 'EXPENSE', labelKey: 'category.e-zp', color: '#b85ec7' },
  { id: 'e-util', type: 'EXPENSE', labelKey: 'category.e-util', color: '#1f7ad1' },
  { id: 'e-rent', type: 'EXPENSE', labelKey: 'category.e-rent', color: '#5a6b88' },
  { id: 'e-tax', type: 'EXPENSE', labelKey: 'category.e-tax', color: '#0e1a2c' },
  { id: 'e-other', type: 'EXPENSE', labelKey: 'category.e-other', color: '#94a3b8' },
] as const

/** Categories filtered by transaction type — drives the type-aware category picker. */
export function categoriesByType(type: TxnType): readonly CategoryMeta[] {
  return CATEGORY_TAXONOMY.filter((c) => c.type === type)
}

const CATEGORY_BY_ID: ReadonlyMap<CategoryId, CategoryMeta> = new Map(
  CATEGORY_TAXONOMY.map((c) => [c.id, c]),
)

export function categoryMeta(id: CategoryId): CategoryMeta | undefined {
  return CATEGORY_BY_ID.get(id)
}

// ─── Departments ────────────────────────────────────────────────────────────
// Cost/revenue centre the operation belongs to. labelKey under `accounting`.

export interface DepartmentMeta {
  id: Department
  labelKey: string
}

export const DEPARTMENT_META: Record<Department, DepartmentMeta> = {
  SUPPLY: { id: 'SUPPLY', labelKey: 'department.SUPPLY' },
  TRANSPORT: { id: 'TRANSPORT', labelKey: 'department.TRANSPORT' },
  SALES: { id: 'SALES', labelKey: 'department.SALES' },
  HR: { id: 'HR', labelKey: 'department.HR' },
  FACILITY: { id: 'FACILITY', labelKey: 'department.FACILITY' },
  FINANCE: { id: 'FINANCE', labelKey: 'department.FINANCE' },
}

// ─── Account kinds ────────────────────────────────────────────────────────────
// Physical money location (Касса / Карта / Банк / Валюта). labelKey under `accounting`.

export interface AccountKindMeta {
  id: AccountKind
  labelKey: string
}

export const ACCOUNT_KIND_META: Record<AccountKind, AccountKindMeta> = {
  CASH: { id: 'CASH', labelKey: 'account_kind.CASH' },
  CARD: { id: 'CARD', labelKey: 'account_kind.CARD' },
  BANK: { id: 'BANK', labelKey: 'account_kind.BANK' },
  FX: { id: 'FX', labelKey: 'account_kind.FX' },
}
